/**
 * The gallery METRICS block (GalleryPanelView.ProjectMetrics.cs), computed from the raw
 * measurements.json rows and the centerline polyline. Sections, rows, folding rules and number
 * formatting follow the desktop verbatim. Values are in the file frame (no axis swap) because
 * the block prints raw coordinates.
 */
import type { MeasurementRow } from '@/prediction/measurements';
import { formatAngle, formatMeasurement } from '@/prediction/measurements';

const MISSING = '-';

export interface MetricsRow {
  label: string;
  value: string;
}
export interface MetricsGroup {
  title: string | null;
  rows: MetricsRow[];
}
export interface MetricsSection {
  title: string;
  groups: MetricsGroup[];
  rowCount: number;
}

type Vec = [number, number, number];
type Entry = MeasurementRow;

function v3(r: Entry | null | undefined): Vec | null {
  if (!r) return null;
  const x = r.World_X_mm;
  const y = r.World_Y_mm;
  const z = r.World_Z_mm;
  if (x === null || y === null || z === null) return null;
  return [x, y, z];
}

function fmtEntry(e: Entry | null | undefined): string {
  return e && typeof e.Value === 'number' ? formatMeasurement(e.Value, e.Units) : MISSING;
}
function fmtAngle(e: Entry | null | undefined): string {
  return e && typeof e.Value === 'number' ? formatAngle(Math.abs(e.Value)) : MISSING;
}
function fmtCount(n: number): string {
  return n > 0 ? String(n) : MISSING;
}
function fmtVector(v: Vec | null, dp = 2): string {
  return v ? `[${v[0].toFixed(dp)}, ${v[1].toFixed(dp)}, ${v[2].toFixed(dp)}]` : MISSING;
}
function fmtTilt(n: Vec | null): string {
  if (!n || (n[0] === 0 && n[1] === 0 && n[2] === 0)) return MISSING;
  // degrees from Unity +Y = file z axis (texture y = voxel k)
  const len = Math.hypot(n[0], n[1], n[2]);
  const cos = Math.min(1, Math.max(-1, n[2] / len));
  return formatAngle((Math.acos(cos) * 180) / Math.PI);
}
function fmtEllipticity(max: Entry | null | undefined, min: Entry | null | undefined): string {
  if (!max || !min || typeof max.Value !== 'number' || typeof min.Value !== 'number' || Math.abs(max.Value) <= 1e-6) return MISSING;
  return (Math.abs(min.Value) / Math.abs(max.Value)).toFixed(3);
}
function ringCenter(pts: Vec[]): Vec | null {
  if (!pts.length) return null;
  const c: Vec = [0, 0, 0];
  for (const p of pts) {
    c[0] += p[0];
    c[1] += p[1];
    c[2] += p[2];
  }
  return [c[0] / pts.length, c[1] / pts.length, c[2] / pts.length];
}
function ringNormal(pts: Vec[]): Vec | null {
  if (pts.length < 3) return null;
  for (let i = 0; i + 2 < pts.length; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    const c = pts[i + 2];
    const u: Vec = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
    const w: Vec = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
    const n: Vec = [u[1] * w[2] - u[2] * w[1], u[2] * w[0] - u[0] * w[2], u[0] * w[1] - u[1] * w[0]];
    const l2 = n[0] * n[0] + n[1] * n[1] + n[2] * n[2];
    if (l2 > 1e-6) {
      const l = Math.sqrt(l2);
      return [n[0] / l, n[1] / l, n[2] / l];
    }
  }
  return null;
}

export function computeMetrics(rows: MeasurementRow[], centerline: Vec[] | null): MetricsSection[] {
  let primaryFrame: number | null = null;
  for (const r of rows) {
    if (r.Category === 'Plane' && r.Structure === 'VBR' && r.Name === 'normal_unit') {
      primaryFrame = r.Frame_Index ?? 0;
      break;
    }
  }
  const slots: Record<string, Entry | null> = {};
  const take = (key: string, e: Entry) => {
    const isPrimary = primaryFrame !== null && (e.Frame_Index ?? 0) === primaryFrame;
    if (!slots[key] || isPrimary) slots[key] = e;
  };
  const rings: Record<string, Map<number, Vec[]>> = {};
  const addRing = (s: string, e: Entry) => {
    const p = v3(e);
    if (!p) return;
    const f = e.Frame_Index ?? 0;
    rings[s] ??= new Map();
    const list = rings[s].get(f) ?? [];
    list.push(p);
    rings[s].set(f, list);
  };

  for (const e of rows) {
    const s = e.Structure;
    switch (e.Category) {
      case 'Metric':
        if (s === 'AorticRoot') {
          if (e.Name === 'volume') take('AorticVolume', e);
          if (e.Name === 'surface_area') take('AorticArea', e);
        } else if (s === 'Centerline') {
          if (e.Name === 'curvature') take('CenterlineCurvature', e);
          if (e.Name === 'length') take('CenterlineLength', e);
        } else if (s === 'VBR' || s === 'SOV' || s === 'STJ' || s === 'AscendingAorta') {
          if (e.Name === 'area_projected') take(`${s}Area`, e);
          if (e.Name === 'perimeter_3D') take(`${s}Perimeter`, e);
          if (s === 'STJ' && e.Name === 'height_mean') take('StjHeightMean', e);
          if (s === 'AscendingAorta' && e.Name === 'height_above_stj') take('AscAortaHeight', e);
        } else if (s === 'LSinus' || s === 'RSinus' || s === 'NSinus') {
          if (e.Name === 'volume') take(`${s}Volume`, e);
          if (e.Name === 'surface_area') take(`${s}Area`, e);
        }
        break;
      case 'RingPoint':
        if (['VBR', 'SOV', 'STJ', 'AscendingAorta'].includes(s)) addRing(s, e);
        break;
      case 'Plane':
        if (s === 'VBR') {
          if (e.Name === 'center') take('VbrCenter', e);
          if (e.Name === 'normal_unit') take('VbrNormal', e);
        }
        break;
      case 'Diameter':
        if (['VBR', 'SOV', 'STJ', 'AscendingAorta'].includes(s)) {
          if (e.Name === 'max') take(`${s}Max`, e);
          if (e.Name === 'min') take(`${s}Min`, e);
        }
        break;
      case 'Point':
        if (s === 'Ostium') {
          if (e.Name === 'Left Coronary Ostium') take('LcaOstium', e);
          if (e.Name === 'Right Coronary Ostium') take('RcaOstium', e);
        } else if (s === 'Commissure') {
          if (['RL', 'RN', 'LN'].includes(e.Name)) take(`${e.Name}Commissure`, e);
        } else if (s === 'Coaptation' && e.Name === 'Coaptation') take('Coaptation', e);
        else if (s === 'Nadir' && ['N', 'R', 'L'].includes(e.Name)) take(`${e.Name}Nadir`, e);
        break;
      case 'Distance':
        if (s === 'Intercommissural') {
          const key = e.Name || e.Pair;
          if (key === 'RL-RN') take('DistRlRn', e);
          if (key === 'LN-RL') take('DistRlLn', e);
          if (key === 'LN-RN') take('DistRnLn', e);
        }
        break;
      case 'Angle':
        if (s === 'Commissural') {
          const key = e.Pair || e.Name;
          if (key === 'RN-RL') take('AngleRnRl', e);
          if (key === 'RL-LN') take('AngleRlLn', e);
          if (key === 'LN-RN') take('AngleLnRn', e);
        }
        break;
    }
  }

  const selectRing = (s: string): Vec[] => {
    const m = rings[s];
    if (!m) return [];
    if (primaryFrame !== null && m.has(primaryFrame)) return m.get(primaryFrame)!;
    const lowest = Math.min(...m.keys());
    return m.get(lowest) ?? [];
  };

  const sections: MetricsSection[] = [];
  const group = (title: string | null, ...rows: [string, string][]): MetricsGroup => ({ title, rows: rows.map(([label, value]) => ({ label, value })) });
  const addSection = (title: string, ...groups: (MetricsGroup | null)[]) => {
    let kept = groups.filter((g): g is MetricsGroup => !!g && g.rows.some((r) => r.value !== MISSING));
    // A titled group whose only row is "Value" folds into the untitled group.
    const folded: MetricsGroup[] = [];
    let untitled = kept.find((g) => g.title === null) ?? null;
    for (const g of kept) {
      if (g.title !== null && g.rows.length === 1 && g.rows[0].label === 'Value') {
        if (!untitled) {
          untitled = { title: null, rows: [] };
          folded.unshift(untitled);
        }
        untitled.rows.push({ label: g.title, value: g.rows[0].value });
      } else if (!folded.includes(g)) folded.push(g);
    }
    kept = folded.filter((g) => g.rows.length > 0);
    if (!kept.length) return;
    sections.push({ title, groups: kept, rowCount: kept.reduce((n, g) => n + g.rows.length, 0) });
  };
  const diameterGroup = (title: string, e: Entry | null): MetricsGroup => {
    const p1 = e && e.P1_X_mm !== null && e.P1_Y_mm !== null && e.P1_Z_mm !== null ? ([e.P1_X_mm, e.P1_Y_mm, e.P1_Z_mm] as Vec) : null;
    const p2 = e && e.P2_X_mm !== null && e.P2_Y_mm !== null && e.P2_Z_mm !== null ? ([e.P2_X_mm, e.P2_Y_mm, e.P2_Z_mm] as Vec) : null;
    const zero = (v: Vec | null) => v !== null && v[0] === 0 && v[1] === 0 && v[2] === 0;
    if ((zero(p1) && zero(p2)) || (!p1 && !p2 && e)) return group(title, ['Value', fmtEntry(e)]);
    return group(title, ['Value', fmtEntry(e)], ['Point 1', fmtVector(p1)], ['Point 2', fmtVector(p2)]);
  };
  const pointGroup = (title: string, e: Entry | null, commissure = false): MetricsGroup => {
    if (!e) return group(title, ['X', MISSING], ['Y', MISSING], ['Z', MISSING], ['Height from VBR plane', MISSING]);
    const f = (v: number | null) => (v === null ? MISSING : formatMeasurement(v, e.Units));
    let h = e.Height_to_VBR_mm;
    if (commissure && (h === null || Number.isNaN(h))) h = e.Commissural_Height_mm;
    return group(title, ['X', f(e.World_X_mm)], ['Y', f(e.World_Y_mm)], ['Z', f(e.World_Z_mm)], ['Height from VBR plane', h === null ? MISSING : f(Math.abs(h))]);
  };
  const coordinateGroup = (title: string, e: Entry | null): MetricsGroup => {
    const f = (v: number | null | undefined) => (v === null || v === undefined ? MISSING : formatMeasurement(v, e?.Units ?? 'mm'));
    return group(title, ['X', f(e?.World_X_mm)], ['Y', f(e?.World_Y_mm)], ['Z', f(e?.World_Z_mm)]);
  };
  const g = (k: string) => slots[k] ?? null;

  addSection('AORTIC ROOT', group(null, ['Volume', fmtEntry(g('AorticVolume'))], ['Surface Area', fmtEntry(g('AorticArea'))]));
  addSection(
    'CENTERLINE',
    group(
      null,
      ['Control Points', fmtCount(centerline?.length ?? 0)],
      ['Length', fmtEntry(g('CenterlineLength'))],
      ['Curvature', fmtEntry(g('CenterlineCurvature'))],
      ['Start Point', fmtVector(centerline?.[0] ?? null)],
      ['End Point', fmtVector(centerline?.[centerline.length - 1] ?? null)],
    ),
  );
  addSection('NADIRS', pointGroup('L Nadir', g('LNadir')), pointGroup('N Nadir', g('NNadir')), pointGroup('R Nadir', g('RNadir')));
  const vbrNormal = v3(g('VbrNormal'));
  const vbrRing = selectRing('VBR');
  addSection(
    'VIRTUAL BASAL RING',
    group('Plane', ['Normal Vector', fmtVector(vbrNormal, 3)], ['Center Point', fmtVector(v3(g('VbrCenter')))], ['Tilt Angle', fmtTilt(vbrNormal)]),
    group('Ellipse', ['Perimeter', fmtEntry(g('VBRPerimeter'))], ['Area', fmtEntry(g('VBRArea'))], ['Control Points', fmtCount(vbrRing.length)], ['Ellipticity', fmtEllipticity(g('VBRMax'), g('VBRMin'))]),
    diameterGroup('Maximum Diameter', g('VBRMax')),
    diameterGroup('Minimum Diameter', g('VBRMin')),
  );
  const sovRing = selectRing('SOV');
  addSection(
    'SINUS OF VALSALVA',
    group('Contour', ['Perimeter', fmtEntry(g('SOVPerimeter'))], ['Area', fmtEntry(g('SOVArea'))], ['Control Points', fmtCount(sovRing.length)], ['Ellipticity', fmtEllipticity(g('SOVMax'), g('SOVMin'))]),
    diameterGroup('Maximum Diameter', g('SOVMax')),
    diameterGroup('Minimum Diameter', g('SOVMin')),
    group('L Sinus', ['Volume', fmtEntry(g('LSinusVolume'))], ['Surface Area', fmtEntry(g('LSinusArea'))]),
    group('N Sinus', ['Volume', fmtEntry(g('NSinusVolume'))], ['Surface Area', fmtEntry(g('NSinusArea'))]),
    group('R Sinus', ['Volume', fmtEntry(g('RSinusVolume'))], ['Surface Area', fmtEntry(g('RSinusArea'))]),
  );
  const stjRing = selectRing('STJ');
  const stjNormal = ringNormal(stjRing);
  addSection(
    'SINUTUBULAR JUNCTION',
    group(null, ['Height', fmtEntry(g('StjHeightMean'))]),
    group('Plane', ['Normal Vector', fmtVector(stjNormal, 3)], ['Center Point', fmtVector(ringCenter(stjRing))], ['Tilt Angle', fmtTilt(stjNormal)]),
    group('Contour', ['Perimeter', fmtEntry(g('STJPerimeter'))], ['Area', fmtEntry(g('STJArea'))], ['Control Points', fmtCount(stjRing.length)]),
    diameterGroup('Maximum Diameter', g('STJMax')),
    diameterGroup('Minimum Diameter', g('STJMin')),
  );
  const ascRing = selectRing('AscendingAorta');
  addSection(
    'ASCENDING AORTA',
    group(null, ['Maximum Diameter', fmtEntry(g('AscendingAortaMax'))], ['Minimum Diameter', fmtEntry(g('AscendingAortaMin'))], ['Height above STJ', fmtEntry(g('AscAortaHeight'))]),
    group('Contour', ['Perimeter', fmtEntry(g('AscendingAortaPerimeter'))], ['Area', fmtEntry(g('AscendingAortaArea'))], ['Control Points', fmtCount(ascRing.length)]),
  );
  addSection('CORONARY OSTIA', pointGroup('LCA Ostium', g('LcaOstium')), pointGroup('RCA Ostium', g('RcaOstium')));
  addSection(
    'COMMISSURES',
    pointGroup('RL Commissure', g('RLCommissure'), true),
    pointGroup('RN Commissure', g('RNCommissure'), true),
    pointGroup('LN Commissure', g('LNCommissure'), true),
    group('Intercommissural Lengths', ['RL–RN', fmtEntry(g('DistRlRn'))], ['RL–LN', fmtEntry(g('DistRlLn'))], ['RN–LN', fmtEntry(g('DistRnLn'))]),
    coordinateGroup('Root Centroid Point', g('Coaptation')),
  );
  addSection('COMMISSURAL ANGLES', group(null, ['R Angle', fmtAngle(g('AngleRnRl'))], ['L Angle', fmtAngle(g('AngleRlLn'))], ['N Angle', fmtAngle(g('AngleLnRn'))]));
  return sections;
}
